/**
 * Real Dads Chatbot Widget
 * A self-contained chat widget for therealdads.com Shopify store
 * Version 1.0 — therealdads.com
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIG & CONSTANTS
  // ============================================================================

  const CONFIG = window.RD_CHAT_CONFIG || {
    brandName: 'Real Dads',
    websiteUrl: 'https://therealdads.com',
    wholesaleUrl: 'https://therealdads.faire.com',
    productsJsonUrl: '/assets/rd-products.json',
    jokesJsonUrl: '/assets/rd-dad-jokes.json',
    ga4PropertyId: 'G-XXXXXXXXXX',   // Set via RD_CHAT_CONFIG or gtag on page
    analytics: true,
    leadCapture: true,
    idleNudgeDelay: 30000,
    maxHistoryVisible: 6
  };

  const COLORS = {
    white: '#FFFFFF',
    black: '#1A1A1A',
    offWhite: '#F5F0EB',
    lightGray: '#E8E8E8',
    darkGray: '#666666',
    accent: '#1A1A1A'
  };

  const NAVIGATION = {
    shop: { label: 'Shop', url: CONFIG.websiteUrl },
    about: { label: 'About', url: CONFIG.websiteUrl + '/pages/about' },
    contact: { label: 'Contact', url: CONFIG.websiteUrl + '/pages/contact' },
    faq: { label: 'FAQ', url: CONFIG.websiteUrl + '/pages/faq' },
    wholesale: { label: 'Wholesale', url: CONFIG.wholesaleUrl }
  };

  // Intent priority order
  const INTENTS = {
    PRODUCT_INQUIRY: 1,
    NEW_ARRIVALS: 2,
    NAVIGATION: 3,
    BRAND_QUESTION: 4,
    WHOLESALE: 5,
    DAD_JOKE: 6,
    GREETING: 7,
    SIZING: 8,
    LEAD_CAPTURE: 9,
    THANKS: 10,
    FALLBACK: 11
  };

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const STATE = {
    isOpen: false,
    sessionId: null,
    messageCount: 0,
    started: false,
    usedJokes: new Set(),
    hasShownNudge: false,
    products: [],
    jokes: [],
    messages: []
  };

  // ============================================================================
  // UTILITIES
  // ============================================================================

  const Utils = {
    /**
     * Generate or retrieve session ID from localStorage
     */
    getOrCreateSessionId() {
      let sessionId = localStorage.getItem('rd_chat_session_id');
      if (!sessionId) {
        sessionId = 'rd_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('rd_chat_session_id', sessionId);
      }
      return sessionId;
    },

    /**
     * Save chat state to localStorage
     */
    saveState() {
      const state = {
        messageCount: STATE.messageCount,
        started: STATE.started,
        messages: STATE.messages,
        usedJokes: Array.from(STATE.usedJokes),
        timestamp: Date.now()
      };
      localStorage.setItem('rd_chat_state', JSON.stringify(state));
    },

    /**
     * Load chat state from localStorage
     */
    loadState() {
      const saved = localStorage.getItem('rd_chat_state');
      if (saved) {
        try {
          const state = JSON.parse(saved);
          STATE.messageCount = state.messageCount || 0;
          STATE.started = state.started || false;
          STATE.messages = state.messages || [];
          STATE.usedJokes = new Set(state.usedJokes || []);
          return true;
        } catch (e) {
          console.error('Failed to load chat state:', e);
          return false;
        }
      }
      return false;
    },

    /**
     * Sanitize user input to prevent XSS
     */
    sanitizeText(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /**
     * Convert URLs in text to clickable links (after sanitizing)
     */
    linkify(sanitizedHtml) {
      return sanitizedHtml.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline;">$1</a>'
      );
    },

    /**
     * Clean and normalize response text
     */
    cleanReply(text) {
      if (!text) return '';
      return text
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\n\n+/g, '\n');
    },

    /**
     * Fetch JSON file with error handling
     */
    async fetchJson(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return await response.json();
      } catch (e) {
        console.warn(`Failed to load ${url}:`, e);
        return null;
      }
    },

    /**
     * Detect intent from user message
     */
    detectIntent(message) {
      const msg = message.toLowerCase().trim();

      // New arrivals patterns (check BEFORE general product inquiry)
      if (/\b(new|newest|latest|just dropped|new drop|new arrival|new release|what.?s new|recent|just added|fresh)\b/.test(msg)) {
        return INTENTS.NEW_ARRIVALS;
      }

      // Product inquiry patterns (expanded plurals)
      if (/\b(tee|tees|hat|hats|hoodie|hoodies|shorts|polo|polos|crewneck|crewnecks|shirt|shirts|collection|tennis club|what do you sell|what do you have|show me|looking for|product|products|item|items|merch|apparel|clothing|jacket|jackets|cap|caps|sweatshirt)\b/.test(msg)) {
        return INTENTS.PRODUCT_INQUIRY;
      }

      // Wholesale patterns (HIGH PRIORITY — check before navigation)
      if (/\b(wholesale|bulk|team order|club order|resale|stock|carry your|boutique|buy in bulk|large order|faire)\b/.test(msg)) {
        return INTENTS.WHOLESALE;
      }

      // Brand question patterns (check BEFORE navigation to prevent "about" collision)
      if (/\b(what is real dads|about the brand|who are you|what.?s this about|tell me about (the |real|your)|what does real dads|your story|mission|values|the brand)\b/.test(msg)) {
        return INTENTS.BRAND_QUESTION;
      }

      // Navigation patterns (trimmed to avoid colliding with brand questions)
      if (/\b(where (can|do)|find|shop\b|collections|contact|faq|help me find|take me to|link to|browse|navigate)\b/.test(msg)) {
        return INTENTS.NAVIGATION;
      }

      // Dad joke patterns
      if (/\b(joke|dad joke|make me laugh|funny|tell me|another one|one more|laugh)\b/.test(msg)) {
        return INTENTS.DAD_JOKE;
      }

      // Sizing patterns
      if (/\b(size|sizing|fit|true to size|large|medium|small|xl|xxl|xs|s|m|l|xl|what size|size chart|how does it fit)\b/.test(msg)) {
        return INTENTS.SIZING;
      }

      // Lead capture patterns
      if (/\b(notify|email|updates|new drops|subscribe|mailing list|newsletter|keep me posted|let me know when)\b/.test(msg)) {
        return INTENTS.LEAD_CAPTURE;
      }

      // Greeting patterns
      if (/\b(hi|hello|hey|what.?s up|sup|yo|good morning|good afternoon|greetings|welcome)\b/.test(msg)) {
        return INTENTS.GREETING;
      }

      // Thanks/goodbye patterns
      if (/\b(thanks|thank you|bye|goodbye|later|peace|appreciate|good bye|see ya|take care)\b/.test(msg)) {
        return INTENTS.THANKS;
      }

      return INTENTS.FALLBACK;
    },

    /**
     * Get random item from array
     */
    random(arr) {
      return arr[Math.floor(Math.random() * arr.length)];
    }
  };

  // ============================================================================
  // ANALYTICS
  // ============================================================================

  const Analytics = {
    /**
     * Log event to GA4 + custom window events + localStorage
     */
    log(eventName, data = {}) {
      if (!CONFIG.analytics) return;

      const event = {
        name: eventName,
        timestamp: Date.now(),
        sessionId: STATE.sessionId,
        page_path: window.location.pathname,
        ...data
      };

      // Log to console in dev mode
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
        console.log('[RD Chat]', eventName, data);
      }

      // Fire GA4 events via gtag()
      try {
        if (typeof gtag === 'function') {
          gtag('event', eventName, {
            event_category: data.event_category || 'chatbot',
            event_label: data.event_label || eventName,
            chat_session_id: STATE.sessionId,
            ...data
          });
        }
      } catch (_e) {}

      // Fire custom window event for external listeners
      window.dispatchEvent(new CustomEvent('rd-chat-event', { detail: event }));

      // Store analytics in localStorage
      try {
        const key = 'rd_chat_analytics';
        const stored = JSON.parse(localStorage.getItem(key) || '{}');
        stored[eventName] = (stored[eventName] || 0) + 1;
        localStorage.setItem(key, JSON.stringify(stored));
      } catch (_e) {}
    }
  };

  // ============================================================================
  // PAGE CONTEXT — detects Shopify page type for contextual responses
  // ============================================================================

  const PageContext = {
    /**
     * Detect current Shopify page type
     */
    detectPageType() {
      const path = window.location.pathname;
      if (path === '/' || path === '') return 'home';
      if (path.startsWith('/products/')) return 'product';
      if (path.startsWith('/collections/')) return 'collection';
      if (path.startsWith('/pages/')) return 'page';
      if (path.startsWith('/cart')) return 'cart';
      return 'other';
    },

    /**
     * Build page context object
     */
    build() {
      const path = window.location.pathname;
      const heading = document.querySelector('h1');
      return {
        current_url: window.location.href,
        path: path,
        title: document.title || '',
        heading: heading ? heading.textContent.trim() : '',
        page_type: PageContext.detectPageType(),
        productHandle: path.startsWith('/products/') ? path.replace('/products/', '').split('?')[0] : '',
        collectionHandle: path.startsWith('/collections/') ? path.replace('/collections/', '').split('?')[0] : '',
        visited_at: new Date().toISOString(),
      };
    },

    /**
     * Get the current page context (cached per page load)
     */
    current: null,
    get() {
      if (!PageContext.current) {
        PageContext.current = PageContext.build();
      }
      return PageContext.current;
    }
  };

  // ============================================================================
  // DOM BUILDER & CSS INJECTION
  // ============================================================================

  const DOM = {
    /**
     * Inject all CSS into the page
     */
    injectStyles() {
      const styleId = 'rd-chatbot-styles';
      if (document.getElementById(styleId)) return;

      const css = `
        /* Chat Widget Container */
        #rd-chatbot-widget {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 380px;
          height: 0;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          background: ${COLORS.white};
          display: flex;
          flex-direction: column;
          overflow: hidden;
          z-index: 999999;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          transition: height 0.3s ease, opacity 0.3s ease;
          opacity: 0;
          pointer-events: none;
        }

        #rd-chatbot-widget.open {
          height: 600px;
          opacity: 1;
          pointer-events: auto;
        }

        #rd-chatbot-widget.mobile {
          width: 100%;
          height: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 16px 16px 0 0;
        }

        #rd-chatbot-widget.mobile.open {
          height: 100vh;
          height: 100dvh;
        }

        /* Header */
        .rd-chat-header {
          padding: 16px;
          background: ${COLORS.white};
          border-bottom: 1px solid ${COLORS.lightGray};
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .rd-chat-title {
          font-size: 16px;
          font-weight: 600;
          color: ${COLORS.black};
          margin: 0;
        }

        .rd-chat-close {
          background: none;
          border: none;
          font-size: 20px;
          color: ${COLORS.darkGray};
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }

        .rd-chat-close:hover {
          color: ${COLORS.black};
        }

        /* Messages Container */
        .rd-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: ${COLORS.white};
        }

        .rd-chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .rd-chat-messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .rd-chat-messages::-webkit-scrollbar-thumb {
          background: ${COLORS.lightGray};
          border-radius: 3px;
        }

        .rd-chat-messages::-webkit-scrollbar-thumb:hover {
          background: ${COLORS.darkGray};
        }

        /* View History Link */
        .rd-chat-view-history {
          text-align: center;
          padding: 8px;
          font-size: 12px;
          color: ${COLORS.darkGray};
          cursor: pointer;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .rd-chat-view-history:hover {
          color: ${COLORS.black};
        }

        /* Message Styles */
        .rd-message {
          display: flex;
          gap: 8px;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .rd-message.user {
          justify-content: flex-end;
        }

        .rd-message-content {
          max-width: 75%;
          padding: 12px;
          border-radius: 12px;
          word-wrap: break-word;
          overflow-wrap: break-word;
          line-height: 1.4;
          font-size: 14px;
        }

        .rd-message-content a {
          color: inherit;
          text-decoration: underline;
          word-break: break-all;
        }

        .rd-message.bot .rd-message-content {
          background: ${COLORS.offWhite};
          color: ${COLORS.black};
        }

        .rd-message.user .rd-message-content {
          background: ${COLORS.black};
          color: ${COLORS.white};
        }

        /* Typing Indicator */
        .rd-typing-indicator {
          display: flex;
          gap: 4px;
          padding: 12px;
          background: ${COLORS.offWhite};
          border-radius: 12px;
          width: fit-content;
        }

        .rd-typing-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: ${COLORS.darkGray};
          animation: typing 1.4s infinite;
        }

        .rd-typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }

        .rd-typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 60%, 100% {
            opacity: 0.3;
            transform: translateY(0);
          }
          30% {
            opacity: 1;
            transform: translateY(-8px);
          }
        }

        /* Quick Reply Chips */
        .rd-quick-replies {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }

        .rd-chip {
          padding: 8px 12px;
          border: 1px solid ${COLORS.lightGray};
          border-radius: 20px;
          background: ${COLORS.white};
          color: ${COLORS.black};
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .rd-chip:hover {
          background: ${COLORS.offWhite};
          border-color: ${COLORS.black};
        }

        .rd-chip:active {
          transform: scale(0.98);
        }

        /* Input Area */
        .rd-chat-input-area {
          padding: 12px;
          border-top: 1px solid ${COLORS.lightGray};
          display: flex;
          gap: 8px;
          flex-shrink: 0;
          background: ${COLORS.white};
        }

        .rd-input-field {
          flex: 1;
          border: 1px solid ${COLORS.lightGray};
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 16px;
          font-family: inherit;
          transition: border-color 0.2s;
          -webkit-appearance: none;
        }

        .rd-input-field:focus {
          outline: none;
          border-color: ${COLORS.black};
        }

        .rd-send-button {
          background: ${COLORS.black};
          border: none;
          color: ${COLORS.white};
          padding: 10px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
          flex-shrink: 0;
        }

        .rd-send-button:hover {
          background: ${COLORS.darkGray};
        }

        .rd-send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Chat Bubble Toggle */
        #rd-chatbot-bubble {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: ${COLORS.black};
          color: #FFFFFF;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s;
          z-index: 999998;
          padding: 0;
        }

        #rd-chatbot-bubble:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        }

        #rd-chatbot-bubble.open {
          opacity: 0;
          pointer-events: none;
        }

        /* Unread Badge */
        .rd-unread-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #FF4444;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
        }

        /* Toast/Nudge */
        .rd-toast {
          position: fixed;
          bottom: 90px;
          right: 20px;
          background: ${COLORS.black};
          color: ${COLORS.white};
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 13px;
          z-index: 999997;
          animation: toastIn 0.3s ease;
          max-width: 300px;
        }

        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Modal for Email Capture */
        .rd-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 1000000;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .rd-modal-content {
          background: ${COLORS.white};
          padding: 20px;
          border-radius: 16px 16px 0 0;
          width: 100%;
          max-width: 400px;
          box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
        }

        .rd-modal-title {
          font-size: 16px;
          font-weight: 600;
          color: ${COLORS.black};
          margin-bottom: 8px;
        }

        .rd-modal-text {
          font-size: 13px;
          color: ${COLORS.darkGray};
          margin-bottom: 16px;
          line-height: 1.5;
        }

        .rd-modal-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid ${COLORS.lightGray};
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 12px;
          box-sizing: border-box;
        }

        .rd-modal-buttons {
          display: flex;
          gap: 8px;
        }

        .rd-modal-button {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .rd-modal-button.primary {
          background: ${COLORS.black};
          color: ${COLORS.white};
        }

        .rd-modal-button.primary:hover {
          background: ${COLORS.darkGray};
        }

        .rd-modal-button.secondary {
          background: ${COLORS.offWhite};
          color: ${COLORS.black};
          border: 1px solid ${COLORS.lightGray};
        }

        .rd-modal-button.secondary:hover {
          background: ${COLORS.lightGray};
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          #rd-chatbot-widget {
            width: 100%;
            max-width: 100vw;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 16px 16px 0 0;
          }

          #rd-chatbot-widget.open {
            height: 100vh;
            height: 100dvh;
          }

          .rd-chat-messages {
            padding: 12px;
          }

          .rd-message-content {
            max-width: 85%;
            font-size: 15px;
            padding: 10px;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .rd-message-content a {
            word-break: break-all;
          }

          .rd-chip {
            padding: 10px 14px;
            font-size: 14px;
          }

          .rd-quick-replies {
            flex-wrap: wrap;
          }

          #rd-chatbot-bubble {
            width: 56px;
            height: 56px;
            bottom: 16px;
            right: 16px;
          }

          .rd-toast {
            bottom: 70px;
            right: 16px;
            left: 16px;
            max-width: none;
          }

          .rd-chat-input-area {
            padding: 10px;
            padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
          }

          .rd-input-field {
            font-size: 16px;
          }
        }

        /* iPhone 13 Mini and smaller (375px and below) */
        @media (max-width: 375px) {
          .rd-chat-header {
            padding: 12px;
          }

          .rd-chat-messages {
            padding: 10px;
            gap: 10px;
          }

          .rd-message-content {
            max-width: 88%;
            font-size: 14px;
            padding: 9px;
          }

          .rd-chip {
            padding: 8px 12px;
            font-size: 13px;
          }

          .rd-send-button {
            padding: 10px 12px;
          }
        }
      `;

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = css;
      document.head.appendChild(style);
    },

    /**
     * Create SVG wave icon
     */
    createWaveIcon() {
      return `
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          <path d="M9 10h.01" stroke="#FFFFFF" fill="#FFFFFF" opacity="0.6"></path>
          <path d="M12 10h.01" stroke="#FFFFFF" fill="#FFFFFF" opacity="0.6"></path>
          <path d="M15 10h.01" stroke="#FFFFFF" fill="#FFFFFF" opacity="0.6"></path>
        </svg>
      `;
    },

    /**
     * Create main widget DOM
     */
    createWidget() {
      // Create main widget container
      const widget = document.createElement('div');
      widget.id = 'rd-chatbot-widget';
      widget.setAttribute('role', 'dialog');
      widget.setAttribute('aria-label', 'Real Dads Chat');

      widget.innerHTML = `
        <div class="rd-chat-header">
          <h1 class="rd-chat-title">${CONFIG.brandName}</h1>
          <button class="rd-chat-close" aria-label="Close chat" title="Close">
            <span>✕</span>
          </button>
        </div>
        <div class="rd-chat-messages" role="log" aria-live="polite" aria-atomic="false"></div>
        <div class="rd-chat-input-area">
          <input
            type="text"
            class="rd-input-field"
            placeholder="Message..."
            aria-label="Chat message input"
          />
          <button class="rd-send-button" aria-label="Send message" title="Send">
            Send
          </button>
        </div>
      `;

      // Create bubble toggle
      const bubble = document.createElement('button');
      bubble.id = 'rd-chatbot-bubble';
      bubble.setAttribute('aria-label', 'Open chat');
      bubble.setAttribute('aria-expanded', 'false');
      bubble.innerHTML = this.createWaveIcon();

      // Append to page
      document.body.appendChild(widget);
      document.body.appendChild(bubble);

      return { widget, bubble };
    }
  };

  // ============================================================================
  // MESSAGE HANDLING
  // ============================================================================

  const Messages = {
    /**
     * Add message to UI and state
     */
    add(text, isUser = false, chips = []) {
      const messagesContainer = document.querySelector('.rd-chat-messages');
      if (!messagesContainer) return;

      const messageDiv = document.createElement('div');
      messageDiv.className = `rd-message ${isUser ? 'user' : 'bot'}`;

      const content = document.createElement('div');
      content.className = 'rd-message-content';
      if (isUser) {
        content.textContent = Utils.sanitizeText(text);
      } else {
        // Bot messages: sanitize then linkify URLs
        content.innerHTML = Utils.linkify(Utils.sanitizeText(text));
      }

      messageDiv.appendChild(content);
      messagesContainer.appendChild(messageDiv);

      // Add chips if provided
      if (chips.length > 0 && !isUser) {
        const chipsDiv = document.createElement('div');
        chipsDiv.className = 'rd-quick-replies';

        chips.forEach(chip => {
          const chipBtn = document.createElement('button');
          chipBtn.className = 'rd-chip';
          chipBtn.textContent = chip.label;
          chipBtn.onclick = () => {
            Chat.handleMessage(chip.label);
          };
          chipsDiv.appendChild(chipBtn);
        });

        messageDiv.appendChild(chipsDiv);
      }

      // Store in state
      STATE.messages.push({
        text: text,
        isUser: isUser,
        timestamp: Date.now()
      });

      // Auto-scroll to bottom
      messagesContainer.scrollTop = messagesContainer.scrollHeight;

      // Show history link if needed
      Messages.updateHistoryLink();
    },

    /**
     * Show typing indicator
     */
    showTyping() {
      const messagesContainer = document.querySelector('.rd-chat-messages');
      if (!messagesContainer) return;

      const typing = document.createElement('div');
      typing.className = 'rd-message bot';
      typing.id = 'rd-typing-indicator';

      const indicator = document.createElement('div');
      indicator.className = 'rd-typing-indicator';
      indicator.innerHTML = '<div class="rd-typing-dot"></div><div class="rd-typing-dot"></div><div class="rd-typing-dot"></div>';

      typing.appendChild(indicator);
      messagesContainer.appendChild(typing);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    },

    /**
     * Remove typing indicator
     */
    hideTyping() {
      const typing = document.getElementById('rd-typing-indicator');
      if (typing) typing.remove();
    },

    /**
     * Update history link visibility
     */
    updateHistoryLink() {
      const messagesContainer = document.querySelector('.rd-chat-messages');
      if (!messagesContainer) return;

      let historyLink = messagesContainer.querySelector('.rd-chat-view-history');
      const messageCount = STATE.messages.length;

      if (messageCount > CONFIG.maxHistoryVisible && !historyLink) {
        historyLink = document.createElement('div');
        historyLink.className = 'rd-chat-view-history';
        historyLink.textContent = 'View earlier messages';
        historyLink.onclick = () => Messages.showFullHistory();
        messagesContainer.insertBefore(historyLink, messagesContainer.firstChild);
      }
    },

    /**
     * Show full history (expand from max visible)
     */
    showFullHistory() {
      const link = document.querySelector('.rd-chat-view-history');
      if (link) link.style.display = 'none';
    }
  };

  // ============================================================================
  // CHAT LOGIC
  // ============================================================================

  const Chat = {
    /**
     * Handle incoming user message
     */
    handleMessage(userMessage) {
      if (!userMessage.trim()) return;

      STATE.messageCount++;
      Analytics.log('message_sent', { messageCount: STATE.messageCount });

      // Show user message
      Messages.add(userMessage, true);

      // Show typing indicator
      Messages.showTyping();

      // Detect intent and generate response
      const intent = Utils.detectIntent(userMessage);
      Analytics.log('intent_detected', { intent: intent });

      // Delay response slightly for natural feel
      const delay = 600 + Math.random() * 600;
      setTimeout(() => {
        Messages.hideTyping();
        const response = Chat.generateResponse(userMessage, intent);

        if (response.text) {
          Messages.add(response.text, false, response.chips || []);
        }

        Utils.saveState();
      }, delay);
    },

    /**
     * Generate response based on intent
     */
    generateResponse(message, intent) {
      const msg = message.toLowerCase();

      switch (intent) {
        case INTENTS.PRODUCT_INQUIRY:
          return Chat.handleProductInquiry(message);

        case INTENTS.NEW_ARRIVALS:
          return Chat.handleNewArrivals();

        case INTENTS.WHOLESALE:
          Analytics.log('wholesale_inquiry');
          return {
            text: "For wholesale orders, we handle that through our Faire account. Here's where you can get set up: " + CONFIG.wholesaleUrl,
            chips: [
              { label: 'Shop retail', action: 'navigate-shop' },
              { label: 'Dad joke', action: 'joke' }
            ]
          };

        case INTENTS.BRAND_QUESTION:
          return {
            text: "Real Dads is about showing up — on the court and at home. The Tennis Club collection builds on that idea through clean, everyday pieces inspired by the game.",
            chips: [
              { label: 'Shop the collection', action: 'navigate-shop' },
              { label: 'Dad joke', action: 'joke' }
            ]
          };

        case INTENTS.DAD_JOKE:
          return Chat.handleJoke();

        case INTENTS.GREETING:
          return {
            text: "Hey — welcome in. What can I help you with?",
            chips: [
              { label: 'Shop the collection', action: 'shop' },
              { label: 'Tell me about the brand', action: 'brand' },
              { label: 'Dad joke please', action: 'joke' }
            ]
          };

        case INTENTS.SIZING:
          return {
            text: "For the best fit info, check the size guide on each product page. Our pieces are designed with a relaxed, comfortable fit in mind.",
            chips: [
              { label: 'Shop the collection', action: 'shop' },
              { label: 'Back to questions', action: 'help' }
            ]
          };

        case INTENTS.LEAD_CAPTURE:
          return Chat.handleLeadCapture();

        case INTENTS.THANKS:
          return {
            text: Utils.random([
              "Appreciate you stopping by.",
              "Take care.",
              "Anytime. See you soon."
            ]),
            chips: [
              { label: 'Dad joke', action: 'joke' },
              { label: 'Back to shopping', action: 'shop' }
            ]
          };

        case INTENTS.NAVIGATION:
          return Chat.handleNavigation(msg);

        default:
          return {
            text: "I'm here to help with products, sizing, wholesale, or brand info. What are you looking for?",
            chips: [
              { label: 'Shop the collection', action: 'shop' },
              { label: 'Wholesale info', action: 'wholesale' },
              { label: 'Dad joke', action: 'joke' }
            ]
          };
      }
    },

    /**
     * Handle product inquiry
     */
    handleProductInquiry(message) {
      const msg = message.toLowerCase();

      // Check for pricing questions first — never guess prices
      if (/\b(price|pricing|cost|how much|per unit|\$)\b/.test(msg)) {
        // Find a product match to link to
        const match = Chat.scoreProducts(msg)[0];
        if (match) {
          return {
            text: `Pricing is on the product page — here's the link: ${match.url}`,
            chips: [
              { label: 'See more products', action: 'shop' },
              { label: 'Wholesale pricing', action: 'wholesale' }
            ]
          };
        }
        return {
          text: "Pricing is on each product page. Check the collection here: " + (NAVIGATION.tennis_club ? NAVIGATION.tennis_club.url : CONFIG.websiteUrl),
          chips: [
            { label: 'Shop the collection', action: 'shop' },
            { label: 'Wholesale pricing', action: 'wholesale' }
          ]
        };
      }

      // Score and match products using tag-based scoring
      const matched = Chat.scoreProducts(msg);

      if (matched.length === 1) {
        const p = matched[0];
        return {
          text: `${p.name} — ${p.description} Check it out here: ${p.url}`,
          chips: [
            { label: 'See more products', action: 'shop' },
            { label: 'Wholesale info', action: 'wholesale' },
            { label: 'Dad joke', action: 'joke' }
          ]
        };
      }

      if (matched.length > 1) {
        let text = "Here's what I'd point you to:\n";
        matched.slice(0, 4).forEach(p => {
          text += `\n${p.name} — ${p.url}`;
        });
        if (matched.length > 4) {
          text += `\n\n...and ${matched.length - 4} more in the collection.`;
        }
        return {
          text: text.trim(),
          chips: [
            { label: 'See full collection', action: 'shop' },
            { label: 'Dad joke', action: 'joke' }
          ]
        };
      }

      // General collection inquiry
      if (/\b(collection|tennis club|what do you (sell|have)|show me|everything)\b/.test(msg)) {
        const collectionUrl = NAVIGATION.tennis_club ? NAVIGATION.tennis_club.url : CONFIG.websiteUrl;
        return {
          text: "The Tennis Club collection is our current lineup — clean, everyday pieces inspired by the game. Browse it all here: " + collectionUrl,
          chips: [
            { label: 'Tell me about the brand', action: 'brand' },
            { label: 'Dad joke', action: 'joke' }
          ]
        };
      }

      return {
        text: "I don't want to point you to something we don't have. Check out the full collection here: " + (NAVIGATION.tennis_club ? NAVIGATION.tennis_club.url : CONFIG.websiteUrl),
        chips: [
          { label: 'View collection', action: 'shop' },
          { label: 'Wholesale', action: 'wholesale' }
        ]
      };
    },

    /**
     * Handle new arrivals request — show products tagged "new"
     */
    handleNewArrivals() {
      Analytics.log('new_arrivals_inquiry');

      const newProducts = STATE.products.filter(p => {
        const tags = (p.tags || []).map(t => t.toLowerCase());
        return tags.includes('new');
      });

      if (newProducts.length === 0) {
        return {
          text: "Check out the latest drops in the collection: " + CONFIG.websiteUrl + "/collections/all",
          chips: [
            { label: 'Shop all', action: 'shop' },
            { label: 'Dad joke', action: 'joke' }
          ]
        };
      }

      let text = "Just dropped — here's what's new:\n";
      newProducts.slice(0, 5).forEach(p => {
        text += `\n${p.name} — ${p.url}`;
      });
      if (newProducts.length > 5) {
        text += `\n\n...plus ${newProducts.length - 5} more. Browse them all at ${CONFIG.websiteUrl}/collections/all`;
      }

      return {
        text: text.trim(),
        chips: [
          { label: 'Shop all products', action: 'shop' },
          { label: 'Wholesale info', action: 'wholesale' },
          { label: 'Dad joke', action: 'joke' }
        ]
      };
    },

    /**
     * Score products against user message using weighted tag matching
     */
    scoreProducts(msg) {
      if (STATE.products.length === 0) return [];

      // Expand synonyms so "hoodie" matches "crewneck/sweatshirt" etc.
      const synonyms = {
        hoodie: ['crewneck', 'sweatshirt'],
        hoodies: ['crewneck', 'sweatshirt'],
        sweater: ['crewneck', 'sweatshirt'],
        tee: ['t-shirt', 'shirt'],
        cap: ['hat'],
        snapback: ['hat', 'cap']
      };
      let expandedMsg = msg;
      Object.keys(synonyms).forEach(term => {
        if (msg.includes(term)) {
          expandedMsg += ' ' + synonyms[term].join(' ');
        }
      });

      const scored = STATE.products.map(product => {
        let score = 0;
        const name = (product.name || '').toLowerCase();
        const desc = (product.description || '').toLowerCase();
        const category = (product.category || '').toLowerCase();
        const tags = (product.tags || []).map(t => t.toLowerCase());

        // Direct name match (highest weight)
        if (expandedMsg.includes(name)) score += 10;

        // Category match
        if (expandedMsg.includes(category)) score += 5;

        // Tag matches
        tags.forEach(tag => {
          if (expandedMsg.includes(tag)) score += 3;
        });

        // Keyword matches in name
        const words = expandedMsg.split(/\s+/).filter(w => w.length > 2);
        words.forEach(word => {
          if (name.includes(word)) score += 4;
          if (desc.includes(word)) score += 1;
        });

        return { ...product, score };
      });

      return scored
        .filter(p => p.score > 0)
        .sort((a, b) => b.score - a.score);
    },

    /**
     * Handle dad joke request
     */
    handleJoke() {
      Analytics.log('dad_joke_requested');

      if (STATE.jokes.length === 0) {
        return {
          text: "I've got jokes, but they're still loading. Check back in a sec!",
          chips: [
            { label: 'Shop', action: 'shop' },
            { label: 'About Real Dads', action: 'brand' }
          ]
        };
      }

      // Get random unused joke
      let joke = null;
      const available = STATE.jokes.filter(j => !STATE.usedJokes.has(j.id || j.setup));

      if (available.length === 0) {
        // All jokes used, reset and pick random
        STATE.usedJokes.clear();
        joke = STATE.jokes[Math.floor(Math.random() * STATE.jokes.length)];
      } else {
        joke = available[Math.floor(Math.random() * available.length)];
      }
      STATE.usedJokes.add(joke.id || joke.setup);

      // Return joke setup first
      const setupText = joke.setup || joke.text;
      setTimeout(() => {
        Messages.add("...", false);
        setTimeout(() => {
          // Replace ... with punchline
          const messagesContainer = document.querySelector('.rd-chat-messages');
          const lastMsg = messagesContainer.lastChild;
          if (lastMsg && lastMsg.querySelector('.rd-message-content')) {
            lastMsg.querySelector('.rd-message-content').textContent = joke.punchline || joke.text;
            lastMsg.parentElement.appendChild(
              (() => {
                const chipsDiv = document.createElement('div');
                chipsDiv.className = 'rd-quick-replies';

                const chip1 = document.createElement('button');
                chip1.className = 'rd-chip';
                chip1.textContent = 'Another one';
                chip1.onclick = () => {
                  Chat.handleMessage('another joke');
                };

                const chip2 = document.createElement('button');
                chip2.className = 'rd-chip';
                chip2.textContent = 'Back to shopping';
                chip2.onclick = () => {
                  Chat.handleMessage('show me products');
                };

                chipsDiv.appendChild(chip1);
                chipsDiv.appendChild(chip2);
                return chipsDiv;
              })()
            );
          }
        }, 800);
      }, 800);

      return {
        text: setupText,
        chips: []
      };
    },

    /**
     * Handle lead capture
     */
    handleLeadCapture() {
      // Show email modal
      const overlay = document.createElement('div');
      overlay.className = 'rd-modal-overlay';

      const content = document.createElement('div');
      content.className = 'rd-modal-content';
      content.innerHTML = `
        <div class="rd-modal-title">Stay in the loop</div>
        <div class="rd-modal-text">Get notified about new drops and releases.</div>
        <input type="email" class="rd-modal-input" placeholder="your@email.com" />
        <div class="rd-modal-buttons">
          <button class="rd-modal-button secondary" onclick="this.closest('.rd-modal-overlay').remove()">
            Maybe later
          </button>
          <button class="rd-modal-button primary">Subscribe</button>
        </div>
      `;

      overlay.appendChild(content);
      document.body.appendChild(overlay);

      // Handle subscribe
      const submitBtn = content.querySelector('.rd-modal-button.primary');
      const input = content.querySelector('.rd-modal-input');

      submitBtn.onclick = () => {
        const email = input.value.trim();
        if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          // Store email
          let emails = JSON.parse(localStorage.getItem('rd_chat_emails') || '[]');
          if (!emails.includes(email)) {
            emails.push(email);
            localStorage.setItem('rd_chat_emails', JSON.stringify(emails));
          }

          Analytics.log('lead_captured', { email: email });

          overlay.remove();
          Messages.add("Got it — we'll keep you in the loop.", false, [
            { label: 'Back to shopping', action: 'shop' }
          ]);
        } else {
          input.style.borderColor = '#FF4444';
        }
      };

      return {
        text: "I'll show you a form to sign up.",
        chips: []
      };
    },

    /**
     * Handle navigation
     */
    handleNavigation(msg) {
      if (msg.includes('about')) {
        return {
          text: "Learn more about the brand here: " + NAVIGATION.about.url,
          chips: [
            { label: 'Shop', action: 'shop' },
            { label: 'Wholesale', action: 'wholesale' }
          ]
        };
      }

      if (msg.includes('contact')) {
        return {
          text: "Get in touch here: " + NAVIGATION.contact.url,
          chips: [
            { label: 'Back', action: 'help' }
          ]
        };
      }

      return {
        text: "Check out the collection: " + CONFIG.websiteUrl,
        chips: [
          { label: 'About', action: 'about' },
          { label: 'Wholesale', action: 'wholesale' }
        ]
      };
    }
  };

  // ============================================================================
  // UI INTERACTION
  // ============================================================================

  const UI = {
    /**
     * Initialize widget interactions
     */
    init(elements) {
      const { widget, bubble } = elements;

      // Toggle open/close
      bubble.addEventListener('click', () => {
        UI.toggle(widget, bubble);
      });

      const closeBtn = widget.querySelector('.rd-chat-close');
      closeBtn.addEventListener('click', () => {
        UI.close(widget, bubble);
      });

      // Handle input
      const input = widget.querySelector('.rd-input-field');
      const sendBtn = widget.querySelector('.rd-send-button');

      const sendMessage = () => {
        const message = input.value.trim();
        if (message) {
          Chat.handleMessage(message);
          input.value = '';
          input.focus();
        }
      };

      sendBtn.addEventListener('click', sendMessage);
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          sendMessage();
        }
      });

      // Handle mobile
      UI.detectMobile(widget);
      window.addEventListener('resize', () => UI.detectMobile(widget));
    },

    /**
     * Toggle widget open/close
     */
    toggle(widget, bubble) {
      if (widget.classList.contains('open')) {
        UI.close(widget, bubble);
      } else {
        UI.open(widget, bubble);
      }
    },

    /**
     * Open widget
     */
    open(widget, bubble) {
      widget.classList.add('open');
      bubble.classList.add('open');
      bubble.setAttribute('aria-expanded', 'true');
      Analytics.log('widget_opened');
    },

    /**
     * Close widget
     */
    close(widget, bubble) {
      widget.classList.remove('open');
      bubble.classList.remove('open');
      bubble.setAttribute('aria-expanded', 'false');
      Analytics.log('widget_closed');
    },

    /**
     * Detect mobile and adjust widget
     */
    detectMobile(widget) {
      if (window.innerWidth < 480) {
        widget.classList.add('mobile');
      } else {
        widget.classList.remove('mobile');
      }
    }
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  async function init() {
    // Inject CSS
    DOM.injectStyles();

    // Create DOM
    const elements = DOM.createWidget();

    // Initialize interactions
    UI.init(elements);

    // Load session
    STATE.sessionId = Utils.getOrCreateSessionId();
    const hasLoadedState = Utils.loadState();

    // Load external data (products.json has nested structure, jokes.json has { jokes: [] })
    const [productsData, jokesData] = await Promise.all([
      Utils.fetchJson(CONFIG.productsJsonUrl),
      Utils.fetchJson(CONFIG.jokesJsonUrl)
    ]);

    if (productsData) {
      // Extract products from nested collections structure
      if (productsData.collections) {
        const allProducts = [];
        Object.values(productsData.collections).forEach(collection => {
          if (Array.isArray(collection.products)) {
            collection.products.forEach(p => {
              allProducts.push({ ...p, collectionName: collection.name, collectionUrl: collection.url });
            });
          }
        });
        STATE.products = allProducts;
      } else if (Array.isArray(productsData)) {
        STATE.products = productsData;
      }
      // Also store navigation links
      if (productsData.navigation) {
        Object.assign(NAVIGATION, {
          shop: { label: 'Shop', url: productsData.navigation.shop || CONFIG.websiteUrl },
          collections: { label: 'Collections', url: productsData.navigation.collections || CONFIG.websiteUrl + '/collections' },
          tennis_club: { label: 'Tennis Club', url: productsData.navigation.tennis_club || CONFIG.websiteUrl + '/collections/tennis-club' },
          about: { label: 'About', url: productsData.navigation.about || NAVIGATION.about.url },
          contact: { label: 'Contact', url: productsData.navigation.contact || NAVIGATION.contact.url },
          faq: { label: 'FAQ', url: productsData.navigation.faq || NAVIGATION.faq.url },
        });
      }
      // Store brand info
      if (productsData.brand) {
        STATE.brand = productsData.brand;
      }
    }

    if (jokesData) {
      // Support both { jokes: [] } and raw array formats
      STATE.jokes = Array.isArray(jokesData) ? jokesData : (jokesData.jokes || []);
    }

    // Analytics
    Analytics.log('session_start', {
      isReturning: hasLoadedState,
      sessionId: STATE.sessionId
    });

    // Show first message if new session
    if (!hasLoadedState || STATE.messages.length === 0) {
      STATE.started = true;
      Messages.add("Welcome to Real Dads. Let me know if you need anything.");

      setTimeout(() => {
        Messages.add("Or I can tell you a solid dad joke.", false, [
          { label: 'Tell me one', action: 'joke' },
          { label: "What's new", action: 'new' },
          { label: 'Show me products', action: 'shop' }
        ]);
      }, 1500);
    } else {
      // Restore previous messages
      const messagesContainer = elements.widget.querySelector('.rd-chat-messages');
      STATE.messages.slice(-CONFIG.maxHistoryVisible).forEach(msg => {
        const messageDiv = document.createElement('div');
        messageDiv.className = `rd-message ${msg.isUser ? 'user' : 'bot'}`;

        const content = document.createElement('div');
        content.className = 'rd-message-content';
        if (msg.isUser) {
          content.textContent = Utils.sanitizeText(msg.text);
        } else {
          content.innerHTML = Utils.linkify(Utils.sanitizeText(msg.text));
        }

        messageDiv.appendChild(content);
        messagesContainer.appendChild(messageDiv);
      });

      Messages.updateHistoryLink();
    }

    Utils.saveState();

    // Idle nudge
    setTimeout(() => {
      if (!STATE.hasShownNudge && !elements.widget.classList.contains('open')) {
        const nudge = document.createElement('div');
        nudge.className = 'rd-toast';
        nudge.textContent = STATE.messageCount > 0
          ? "Welcome back. Anything I can help with?"
          : "Need help finding your size?";

        document.body.appendChild(nudge);
        STATE.hasShownNudge = true;

        setTimeout(() => nudge.remove(), 5000);
      }
    }, CONFIG.idleNudgeDelay);
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
